import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import { useNotification } from '../contexts/NotificationContext';
import { useTimezone } from '../contexts/TimezoneContext';
import { formatUiTimeShort } from '../utils/dateUtils';

const PEEK_DURATION_MS = 5000;

interface NotificationPanelProps {
  drawerOpen: boolean;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ drawerOpen }) => {
  const { timezone } = useTimezone();
  const formatTimestamp = (timestamp: string): string => formatUiTimeShort(timestamp, timezone);

  const {
    notifications,
    unreadCount,
    latestNotification,
    dismissNotification,
    clearAllNotifications,
    markAllRead,
  } = useNotification();

  const [expanded, setExpanded] = useState(false);
  const [peekVisible, setPeekVisible] = useState(false);
  const [peekNotificationId, setPeekNotificationId] = useState<string | null>(null);
  // Use a ref to track which notification has already triggered a peek so that
  // updating it doesn't re-run the effect and accidentally cancel the timer.
  const lastPeekedIdRef = useRef<string | null>(null);

  const sortedNotifications = useMemo(
    () => [...notifications].sort((a, b) => b.timestamp.localeCompare(a.timestamp)),
    [notifications]
  );

  // When notifications are cleared (e.g. via Clear All), hide peek and reset refs
  useEffect(() => {
    if (notifications.length === 0) {
      setPeekVisible(false);
      setPeekNotificationId(null);
      lastPeekedIdRef.current = null;
    }
  }, [notifications.length]);

  // Trigger peek when a new notification arrives (and panel is closed)
  useEffect(() => {
    if (!latestNotification || expanded) return;
    if (lastPeekedIdRef.current === latestNotification.id) return;

    lastPeekedIdRef.current = latestNotification.id;
    setPeekNotificationId(latestNotification.id);
    setPeekVisible(true);

    const timer = window.setTimeout(() => setPeekVisible(false), PEEK_DURATION_MS);
    return () => window.clearTimeout(timer);
    // peekNotificationId intentionally omitted — using ref to avoid retriggering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestNotification, expanded]);

  const peekNotification = useMemo(
    () =>
      peekNotificationId
        ? (notifications.find((n) => n.id === peekNotificationId) ?? null)
        : null,
    [notifications, peekNotificationId]
  );

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      if (next) {
        setPeekVisible(false);
        markAllRead();
      }
      return next;
    });
  }, [markAllRead]);

  const handleClearAll = useCallback(() => {
    clearAllNotifications();
    setPeekVisible(false);
    setPeekNotificationId(null);
    lastPeekedIdRef.current = null;
    setExpanded(false);
  }, [clearAllNotifications]);

  const label = 'Notifications';

  return (
    <Box
      sx={(theme) => ({
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.92),
        backdropFilter: 'blur(10px)',
        // Never let the panel overflow the drawer width
        overflow: 'hidden',
      })}
    >
      {/* ── Expanded list ── */}
      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={(theme) => ({
            px: drawerOpen ? 1.25 : 0.5,
            pt: 1,
            pb: 0.5,
            // Fill space from here to the toggle button; cap at 60vh to leave room for nav
            maxHeight: '60vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          {/* Header row */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={drawerOpen ? 'space-between' : 'center'}
            sx={{ mb: 1 }}
          >
            {drawerOpen && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontFamily: '"Space Mono", monospace' }}
              >
                Notification Log
              </Typography>
            )}
            <Tooltip title="Clear all notifications" placement="right">
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClearAll();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClearAll();
                  }
                }}
                style={{
                  cursor: notifications.length > 0 ? 'pointer' : 'default',
                  display: 'inline-flex',
                  pointerEvents: 'auto',
                }}
              >
                <Chip
                  icon={<DeleteSweepOutlinedIcon />}
                  label={drawerOpen ? 'Clear All' : undefined}
                  size="small"
                  disabled={notifications.length === 0}
                  sx={(theme) => ({
                    fontFamily: '"Space Mono", monospace',
                    borderRadius: 1,
                    pointerEvents: 'none',
                    '& .MuiChip-icon': { color: theme.palette.text.secondary },
                  })}
                />
              </span>
            </Tooltip>
          </Stack>

          {/* Notification items */}
          <Stack spacing={0.75}>
            {sortedNotifications.length === 0 ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontFamily: '"Space Mono", monospace', px: 0.5 }}
              >
                No notifications yet.
              </Typography>
            ) : (
              sortedNotifications.map((notification) => (
                <Box
                  key={notification.id}
                  sx={(theme) => ({
                    p: drawerOpen ? 1 : 0.75,
                    borderRadius: 1.5,
                    // Severity-coloured left accent + tinted background
                    borderLeft: `3px solid ${theme.palette[notification.severity].main}`,
                    border: `1px solid ${alpha(theme.palette[notification.severity].main, 0.28)}`,
                    backgroundColor: alpha(
                      theme.palette[notification.severity].main,
                      theme.palette.mode === 'dark' ? 0.13 : 0.08
                    ),
                    // Prevent text from escaping the bubble
                    overflow: 'hidden',
                    minWidth: 0,
                  })}
                >
                  <Stack direction="row" spacing={0.5} alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      {drawerOpen ? (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.primary',
                              fontFamily: '"Space Mono", monospace',
                              fontSize: '0.72rem',
                              lineHeight: 1.4,
                              // Wrap long words / URLs instead of overflowing
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {notification.message}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontFamily: '"Space Mono", monospace',
                              fontSize: '0.65rem',
                            }}
                          >
                            {formatTimestamp(notification.timestamp)}
                          </Typography>
                        </>
                      ) : (
                        <Tooltip title={notification.message} placement="right">
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.primary',
                              fontFamily: '"Space Mono", monospace',
                              fontSize: '0.65rem',
                            }}
                          >
                            {formatTimestamp(notification.timestamp)}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => dismissNotification(notification.id)}
                      aria-label="Dismiss notification"
                      sx={{ color: 'text.secondary', flexShrink: 0 }}
                    >
                      <CloseIcon sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </Stack>
                </Box>
              ))
            )}
          </Stack>
        </Box>
      </Collapse>

      {/* ── Peek: slides down above the toggle button when a new notification arrives ── */}
      <Collapse in={peekVisible && !expanded} unmountOnExit>
        {peekNotification && (
          <Box
            sx={(theme) => ({
              mx: drawerOpen ? 1 : 0.5,
              mt: 0.75,
              mb: 0,
              p: drawerOpen ? 1 : 0.75,
              borderRadius: 1.5,
              borderLeft: `3px solid ${theme.palette[peekNotification.severity].main}`,
              border: `1px solid ${alpha(theme.palette[peekNotification.severity].main, 0.28)}`,
              backgroundColor: alpha(
                theme.palette[peekNotification.severity].main,
                theme.palette.mode === 'dark' ? 0.13 : 0.08
              ),
              overflow: 'hidden',
              minWidth: 0,
            })}
          >
            {drawerOpen ? (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.primary',
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.72rem',
                  display: 'block',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.4,
                }}
              >
                {peekNotification.message}
              </Typography>
            ) : (
              <Tooltip title={peekNotification.message} placement="right">
                <Typography
                  variant="caption"
                  sx={{ color: 'text.primary', fontFamily: '"Space Mono", monospace' }}
                >
                  New
                </Typography>
              </Tooltip>
            )}
          </Box>
        )}
      </Collapse>

      {/* ── Toggle button ── */}
      <Tooltip title={drawerOpen ? '' : label} placement="right" arrow>
        <Box
          onClick={toggleExpanded}
          role="button"
          aria-label={expanded ? 'Collapse notifications' : 'Expand notifications'}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleExpanded();
            }
          }}
          sx={(theme) => ({
            cursor: 'pointer',
            mx: drawerOpen ? 1 : 0.5,
            my: 0.75,
            px: drawerOpen ? 1.25 : 1,
            py: 0.75,
            minHeight: 44,
            borderRadius: 1.5,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
            backgroundColor: alpha(
              theme.palette.primary.main,
              theme.palette.mode === 'dark' ? 0.16 : 0.1
            ),
            display: 'flex',
            alignItems: 'center',
            justifyContent: drawerOpen ? 'space-between' : 'center',
            color: theme.palette.text.primary,
            userSelect: 'none',
            transition: 'background-color 200ms ease-out',
            '&:hover': {
              backgroundColor: alpha(
                theme.palette.primary.main,
                theme.palette.mode === 'dark' ? 0.24 : 0.16
              ),
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          })}
        >
          <Stack direction="row" alignItems="center" spacing={1.75}>
            <Badge
              badgeContent={unreadCount}
              color="error"
              max={99}
              overlap="circular"
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.6rem',
                  minWidth: 14,
                  height: 14,
                  padding: '0 3px',
                },
              }}
            >
              <NotificationsOutlinedIcon sx={{ fontSize: '1.1rem' }} />
            </Badge>
            {drawerOpen && (
              <Typography
                variant="body2"
                sx={{ fontFamily: '"Space Mono", monospace', fontWeight: 600, fontSize: '0.78rem' }}
              >
                {label}
              </Typography>
            )}
          </Stack>
          {drawerOpen &&
            (expanded ? (
              <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            ) : (
              <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            ))}
        </Box>
      </Tooltip>
    </Box>
  );
};

export default NotificationPanel;
